import React from 'react';

import {
  Button,
  Grid,
  Header,
  Label,
  Divider,
  SemanticCOLORS,
  Icon,
} from 'semantic';
import { useQuery } from '@tanstack/react-query';
import { TFunction, useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash-es';

import ThemedSegment from 'components/semantic/Segment';
import {
  TariffRule as CustomTariffRuleType,
  TariffRule,
} from 'subdomains/cpo/types/charging-policy';
import { PriceAndRestrictionsLabel } from 'subdomains/cpo/screens/policies-and-groups/components/Tariff/PriceAndRestrictionsLabel';
import { formatDateForDisplay } from 'subdomains/cpo/utils/date';
import ConfirmDelete from 'components/ConfirmDelete';
import { CustomerGroup } from 'subdomains/cpo/types/charging-policy';
import { request } from 'utils/api';
import { CUSTOMER_GROUPS_V1_SEARCH_API_URL } from 'subdomains/cpo/router/api';
import { getAuthorizationMethodLabel, tariffComponentKey } from './utils';
import { formatAPIError } from 'utils/error';

type Props = {
  values: CustomTariffRuleType;
  currency: string;
  onEdit?: () => void;
  onRemove?: () => Promise<void> | undefined;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
};

export const TariffRulePreview: React.FC<Props> = ({
  values,
  currency,
  onEdit,
  onRemove,
  dragHandleProps,
}) => {
  const { t } = useTranslation();

  const { status: validityStatus, color: validityColor } =
    getValidityPeriodStatus(values.validityPeriod, t);

  const {
    data: customerGroups,
    isLoading: isLoadingCustomerGroups,
    error: errorFetchingCustomerGroups,
  } = useQuery<{
    data: CustomerGroup[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [CUSTOMER_GROUPS_V1_SEARCH_API_URL, values.customerGroups],
    enabled: !isEmpty(values.customerGroups),
    queryFn: async () => {
      const { data, meta } = await request<{
        data: CustomerGroup[];
        meta: { total: number; limit: number };
      }>({
        path: CUSTOMER_GROUPS_V1_SEARCH_API_URL,
        method: 'POST',
        body: {
          ids: values.customerGroups,
        },
      });

      return { data, meta };
    },
  });

  return (
    <ThemedSegment offbackground style={{ paddingBottom: '1.5rem' }}>
      <Grid columns="equal" verticalAlign="middle">
        <Grid.Row centered verticalAlign="middle">
          <Grid.Column
            width={6}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <div {...dragHandleProps} style={{ display: 'flex' }}>
              <Icon size="large" name="arrows-up-down-left-right" />
            </div>
            <h3 style={{ margin: '0 0 0 .8rem' }}>
              {t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.title',
                'Tariff'
              )}
            </h3>

            {validityStatus && validityStatus !== 'Active' && (
              <Label
                basic
                size="large"
                color={validityColor}
                style={{ marginLeft: '1.5rem' }}>
                {validityStatus}
              </Label>
            )}
          </Grid.Column>

          <Grid.Column
            floated="right"
            width={2}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'row',
            }}>
            {onEdit && (
              <Button icon="edit" basic size="mini" onClick={onEdit} />
            )}
            {onRemove && (
              <ConfirmDelete
                onConfirm={onRemove}
                name={t(
                  'providerAdmin.chargingPolicyForm.tariffRulePreview.deletePrompt.headerTariffRuleNameText',
                  'the tariff'
                )}
                objectName={t(
                  'providerAdmin.chargingPolicyForm.tariffRulePreview.deletePrompt.contentTariffRuleNameText',
                  'tariff'
                )}
                trigger={
                  <Button
                    icon="trash"
                    basic
                    size="mini"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                      e.preventDefault()
                    }
                  />
                }
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.authorizationMethods',
                'Payment options:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            {values.authorizationMethods?.length ? (
              values.authorizationMethods?.map((value) => (
                <Label
                  key={value}
                  basic
                  size="large"
                  content={getAuthorizationMethodLabel(t, value)}
                />
              ))
            ) : (
              <Label
                basic
                size="large"
                content={t(
                  `providerAdmin.chargingPolicyForm.tariffRulePreview.allPaymentOptionsLabel`,
                  'All payment options'
                )}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.appliesTo',
                'Applies to:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column
            style={{ display: 'flex', gap: '1rem', flexDirection: 'row' }}>
            {isLoadingCustomerGroups &&
              t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.loadingCustomerGroups',
                'Loading...'
              )}
            {!isLoadingCustomerGroups &&
              errorFetchingCustomerGroups &&
              t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.errorFetchingCustomerGroups',
                'Error: {{error}}',
                { error: formatAPIError(errorFetchingCustomerGroups) }
              )}
            {!isEmpty(customerGroups?.data) ? (
              customerGroups?.data?.map((value) => (
                <CustomerGroupLabel
                  key={value.id}
                  color="blue"
                  content={value.label}
                />
              ))
            ) : (
              <CustomerGroupLabel
                color={'black'}
                content={t(
                  'providerAdmin.chargingPolicyForm.tariffRulePreview.everyone',
                  'Everyone'
                )}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        <Divider />
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.tariffComponents',
                'Tariff:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            {values.tariff?.components?.map((component) => (
              <Label key={tariffComponentKey(component)} basic size="large">
                <PriceAndRestrictionsLabel
                  values={component}
                  currency={currency}
                />
              </Label>
            ))}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
          <Grid.Column width={3}>
            <p>
              {t(
                'providerAdmin.chargingPolicyForm.tariffRulePreview.descriptions',
                'Description:'
              )}
            </p>
          </Grid.Column>
          <Grid.Column>
            {values.tariff?.descriptions?.map((description) => {
              const descriptionKey = `${description.__id}-${description.languageCode}-${description.text}`;

              return (
                <p key={descriptionKey}>
                  {description.languageCode?.toUpperCase()}: {description.text}
                </p>
              );
            })}
          </Grid.Column>
        </Grid.Row>
        {values.validityPeriod?.enabled && (
          <Grid.Row style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
            <Grid.Column width={3}>
              <p>
                {t(
                  'providerAdmin.chargingPolicyForm.tariffRulePreview.valid',
                  'Valid:'
                )}
              </p>
            </Grid.Column>
            <Grid.Column>
              {values.validityPeriod && (
                <Label basic size="large">
                  {validityStatus !== 'Active' && (
                    <Icon name="circle-exclamation" color="red" />
                  )}
                  {formatValidityPeriod(values.validityPeriod, t)}
                </Label>
              )}
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </ThemedSegment>
  );
};

const CustomerGroupLabel = ({
  content,
  color,
}: {
  content: string;
  color: SemanticCOLORS;
}) => {
  return (
    <Header style={{ margin: 0 }} as="h5" color={color} content={content} />
  );
};

export function formatValidityPeriod(
  date: NonNullable<TariffRule['validityPeriod']>,
  t: TFunction<'translation', undefined>
): string {
  if (!date?.enabled || (!date.start && !date.end)) return '';

  if (date.start && date.end) {
    return `${formatDateForDisplay(date.start)}-${formatDateForDisplay(date.end)}`;
  } else if (date.start) {
    return `${t('providerAdmin.chargingPolicyForm.from', 'From')} ${formatDateForDisplay(date.start)}`;
  } else if (date.end) {
    return `${t('providerAdmin.chargingPolicyForm.until', 'Until')} ${formatDateForDisplay(date.end)}`;
  }
  return '';
}

function getValidityPeriodStatus(
  validityPeriod: CustomTariffRuleType['validityPeriod'],
  t: TFunction<'translation', undefined>
): { status: string; color: SemanticCOLORS } {
  if (!validityPeriod || !validityPeriod.enabled) {
    return { status: '', color: 'red' };
  }

  const { start, end } = validityPeriod;

  const now = new Date();
  let status = 'Active';
  let color: SemanticCOLORS = 'green';
  if (start && now < start) {
    status = t(
      'providerAdmin.chargingPolicyForm.tariffRulePreview.validityPeriodNotActiveYet',
      'Not active yet'
    );
    color = 'orange';
  }
  if (end && now > end) {
    status = t(
      'providerAdmin.chargingPolicyForm.tariffRulePreview.validityPeriodExpired',
      'Expired'
    );
    color = 'red';
  }

  return { status, color };
}
